import React, { useEffect, useState } from "react";
import api, { fcfa, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserCog, Inbox, CalendarDays, Crown, Clock } from "lucide-react";

export default function ProviderDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ profession: "", bio: "", services: "", zones: "", base_rate: "", city: "", phone: "", photo_url: "" });
  const [requests, setRequests] = useState([]);
  const [appts, setAppts] = useState([]);
  const [sub, setSub] = useState(null);
  const [quoteFor, setQuoteFor] = useState(null);
  const [quote, setQuote] = useState({ amount: "", message: "" });

  const load = () => {
    api.get("/provider/profile").then((r) => {
      setProfile(r.data);
      if (r.data) setForm({ ...r.data, services: (r.data.services || []).join(", "), zones: (r.data.zones || []).join(", ") });
    });
    api.get("/quote-requests").then((r) => setRequests(r.data));
    api.get("/appointments").then((r) => setAppts(r.data));
    api.get("/subscriptions/me").then((r) => setSub(r.data));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    try {
      await api.post("/provider/profile", {
        ...form, base_rate: form.base_rate ? parseFloat(form.base_rate) : null,
        services: form.services.split(",").map((s) => s.trim()).filter(Boolean),
        zones: form.zones.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Profil enregistré !"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendQuote = async () => {
    try { await api.post("/quotes", { request_id: quoteFor, amount: parseFloat(quote.amount), message: quote.message }); toast.success("Devis envoyé"); setQuoteFor(null); setQuote({ amount: "", message: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const subscribe = async () => {
    const { data } = await api.post("/payments/initiate", { kind: "abonnement", amount: 10000, plan: "pro_prestataire" });
    window.location.href = `/paiement/${data.tx_id}`;
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-secondary">Espace prestataire</h1>
          <p className="text-muted-foreground">{profile?.profession ? `${profile.profession} · ${profile.city}` : "Complétez votre profil."}</p>
        </div>
        {user.status === "pending" && <Badge className="bg-amber-500 text-white">En attente de validation</Badge>}
        {sub?.status === "active" && <Badge className="bg-accent text-accent-foreground"><Crown className="w-3 h-3 mr-1" />Pro actif</Badge>}
      </div>

      <Tabs defaultValue="requests" className="mt-6">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="requests" data-testid="tab-requests"><Inbox className="w-4 h-4 mr-1" />Demandes</TabsTrigger>
          <TabsTrigger value="agenda" data-testid="tab-agenda"><CalendarDays className="w-4 h-4 mr-1" />Agenda</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile"><UserCog className="w-4 h-4 mr-1" />Profil</TabsTrigger>
          <TabsTrigger value="sub" data-testid="tab-sub-p"><Crown className="w-4 h-4 mr-1" />Abonnement</TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="space-y-3 mt-4">
          {requests.length === 0 && <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded-lg">Aucune demande pour le moment.</div>}
          {requests.map((r) => (
            <div key={r.request_id} className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="font-medium text-secondary">{r.service} — {r.client_name}</div>
                <Badge>{r.status}</Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{r.description}</p>
              {r.quotes?.length > 0
                ? <div className="mt-2 text-sm text-accent">Devis envoyé : {fcfa(r.quotes[0].amount)}</div>
                : <Button size="sm" className="mt-3 bg-primary hover:bg-primary/90" data-testid={`quote-req-${r.request_id}`} onClick={() => setQuoteFor(r.request_id)}>Envoyer un devis</Button>}
            </div>
          ))}
          <Dialog open={!!quoteFor} onOpenChange={(o) => !o && setQuoteFor(null)}>
            <DialogContent>
              <DialogHeader><DialogTitle>Envoyer un devis</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Montant (FCFA)</Label><Input data-testid="quote-amount" type="number" value={quote.amount} onChange={(e) => setQuote({ ...quote, amount: e.target.value })} /></div>
                <div><Label>Message</Label><Textarea data-testid="quote-message" value={quote.message} onChange={(e) => setQuote({ ...quote, message: e.target.value })} /></div>
              </div>
              <DialogFooter><Button data-testid="send-quote" className="bg-primary hover:bg-primary/90" onClick={sendQuote}>Envoyer</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="agenda" className="space-y-3 mt-4">
          {appts.length === 0 && <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded-lg">Aucun RDV planifié.</div>}
          {appts.map((a) => (
            <div key={a.appt_id} className="bg-card border border-border rounded-lg p-4 flex items-center justify-between">
              <div>
                <div className="font-medium text-secondary">{a.client_name}</div>
                <div className="text-sm text-muted-foreground flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{a.date} à {a.slot} · {a.service}</div>
              </div>
              <div className="flex items-center gap-2">
                {a.paid && <Badge className="bg-accent text-accent-foreground">Payé {fcfa(a.amount)}</Badge>}
                <Badge>{a.status}</Badge>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="profile" className="mt-4">
          <div className="bg-card border border-border rounded-xl p-6 grid sm:grid-cols-2 gap-4">
            <div><Label>Profession *</Label><Input data-testid="prof-profession" value={form.profession} onChange={set("profession")} /></div>
            <div><Label>Ville</Label><Input value={form.city} onChange={set("city")} /></div>
            <div className="sm:col-span-2"><Label>Bio</Label><Textarea value={form.bio} onChange={set("bio")} /></div>
            <div><Label>Services (séparés par ,)</Label><Input value={form.services} onChange={set("services")} placeholder="Coupe, Tresses" /></div>
            <div><Label>Zones d'intervention (,)</Label><Input value={form.zones} onChange={set("zones")} /></div>
            <div><Label>Tarif de base (FCFA)</Label><Input type="number" value={form.base_rate} onChange={set("base_rate")} /></div>
            <div><Label>Téléphone</Label><Input value={form.phone} onChange={set("phone")} /></div>
            <div className="sm:col-span-2"><Label>Photo (URL)</Label><Input value={form.photo_url} onChange={set("photo_url")} /></div>
            <div className="sm:col-span-2"><Button data-testid="save-profile" className="bg-primary hover:bg-primary/90" onClick={save}>Enregistrer</Button></div>
          </div>
        </TabsContent>

        <TabsContent value="sub" className="mt-4">
          <div className="bg-card border border-border rounded-xl p-8 max-w-md">
            <Crown className="w-10 h-10 text-primary mb-3" />
            <h3 className="font-display text-xl font-bold text-secondary">Abonnement Pro Prestataire</h3>
            <div className="font-display text-3xl font-extrabold text-primary my-3">{fcfa(10000)}<span className="text-sm text-muted-foreground font-normal">/mois</span></div>
            <ul className="text-sm text-muted-foreground space-y-1 mb-5"><li>✓ Mise en avant dans l'annuaire</li><li>✓ Badge vérifié</li><li>✓ Devis & RDV illimités</li></ul>
            {sub?.status === "active"
              ? <Badge className="bg-accent text-accent-foreground">Actif jusqu'au {sub.end?.slice(0, 10)}</Badge>
              : <Button data-testid="subscribe-p" className="w-full bg-primary hover:bg-primary/90" onClick={subscribe}>S'abonner via Mobile Money</Button>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
